import React, { useState, useEffect } from 'react';

interface ChannelsViewProps {
  brand: string;
}

interface ChannelInfo {
  name: string;
  type: string;
  enabled: boolean;
  publicationCount: number;
}

interface Publication {
  slug: string;
  channel: string;
  permalink: string;
  publishedAt: string;
}

export default function ChannelsView({ brand }: ChannelsViewProps) {
  const [channels, setChannels] = useState<ChannelInfo[]>([]);
  const [publications, setPublications] = useState<Publication[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    // Fetch brand config for channel info
    fetch(`/api/${brand}/config`)
      .then((r) => r.json())
      .then((config) => {
        // channels config is at config.channels (top-level), with channel entries nested
        const channelsCfg = config.channels?.channels || config.channels?.defaults?.piece?.routing || {};
        const chList: ChannelInfo[] = Object.entries(channelsCfg).map(([name, cfg]: [string, any]) => ({
          name,
          type: cfg.type || 'unknown',
          enabled: cfg.enabled !== false,
          publicationCount: 0,
        }));

        // Add known channels not in config
        const known = ['site', 'dev-to', 'medium', 'substack', 'reddit', 'linkedin', 'buttondown'];
        for (const k of known) {
          if (!chList.some((c) => c.name === k)) {
            chList.push({ name: k, type: k, enabled: false, publicationCount: 0 });
          }
        }

        setChannels(chList);
        setLoading(false);
      })
      .catch(() => { setChannels([]); setLoading(false); });

    // Fetch publications
    fetch(`/api/${brand}/publications`)
      .then((r) => r.json())
      .then((pubs) => { if (Array.isArray(pubs)) setPublications(pubs.slice(-20).reverse()); })
      .catch(() => {});
  }, [brand]);

  function timeAgo(iso: string): string {
    const diff = Date.now() - new Date(iso).getTime();
    const mins = Math.floor(diff / 60000);
    if (mins < 60) return `${mins}m ago`;
    const hrs = Math.floor(mins / 60);
    if (hrs < 24) return `${hrs}h ago`;
    return `${Math.floor(hrs / 24)}d ago`;
  }

  if (loading) return <div className="view-page"><div style={{ color: 'var(--text-tertiary)' }}>Loading channels…</div></div>;

  return (
    <div className="view-page">
      <h2 className="view-page__title">Channels</h2>

      <div className="channels-list">
        {channels.map((ch) => (
          <div key={ch.name} className="channel-card">
            <div className="channel-card__header">
              <span className="channel-card__name">{ch.name}</span>
              <span className="channel-card__type">{ch.type}</span>
            </div>
            <div className="channel-card__status">
              {ch.enabled ? (
                <span style={{ color: 'var(--success)' }}>{'\u2713'} Active</span>
              ) : (
                <span style={{ color: 'var(--text-tertiary)' }}>{'\u25CB'} Not configured</span>
              )}
            </div>
          </div>
        ))}
      </div>

      {publications.length > 0 && (
        <div style={{ marginTop: 32 }}>
          <h3 className="view-section-title">Recent Publications</h3>
          <div className="publications-list">
            {publications.map((pub, i) => (
              <div key={i} className="publication-row">
                <span className="publication-row__slug">{pub.slug}</span>
                <span className="publication-row__arrow">&rarr;</span>
                <span className="publication-row__channel">{pub.channel}</span>
                <span className="publication-row__time">{timeAgo(pub.publishedAt)}</span>
                {pub.permalink && (
                  <a href={pub.permalink} target="_blank" rel="noopener" className="publication-row__link">View &rarr;</a>
                )}
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
